/**
 * Backup Encryption Service
 * Encrypts sensitive data in backups and provides decryption for restore
 */

/**
 * Encrypt sensitive fields in backup data
 * Uses AES-256-GCM encryption
 */
export const encryptBackupData = async (backupData, encryptionKey) => {
  try {
    // Import encryption key
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(encryptionKey.padEnd(32, '0').slice(0, 32)), // Ensure 32 bytes for AES-256
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    )

    // Fields that contain sensitive data
    const sensitiveFields = {
      clients: ['email', 'phone', 'date_of_birth', 'address', 'emergency_contact'],
      medical_histories: ['current_medications', 'allergies', 'chronic_conditions', 'family_history'],
      examinations: ['vital_signs', 'custom_questions'],
      sessions: ['outcome_summary', 'focus_area'],
      progress_notes: ['note', 'rich_text_content', 'plain_text_content'],
    }

    const encryptedData = JSON.parse(JSON.stringify(backupData)) // Deep clone

    // Encrypt sensitive fields
    for (const [table, fields] of Object.entries(sensitiveFields)) {
      if (encryptedData.data[table]) {
        for (const record of encryptedData.data[table]) {
          for (const field of fields) {
            if (record[field] && typeof record[field] === 'string') {
              const iv = crypto.getRandomValues(new Uint8Array(12)) // 96-bit IV for GCM
              const encoded = new TextEncoder().encode(record[field])
              
              const encrypted = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv },
                key,
                encoded
              )

              // Store as base64 with IV prefix
              const encryptedArray = new Uint8Array(encrypted)
              const combined = new Uint8Array(iv.length + encryptedArray.length)
              combined.set(iv, 0)
              combined.set(encryptedArray, iv.length)
              
              record[field] = `encrypted:${btoa(String.fromCharCode(...combined))}`
            } else if (record[field] && typeof record[field] === 'object') {
              // Handle JSON fields
              const jsonString = JSON.stringify(record[field])
              const iv = crypto.getRandomValues(new Uint8Array(12))
              const encoded = new TextEncoder().encode(jsonString)
              
              const encrypted = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv },
                key,
                encoded
              )

              const encryptedArray = new Uint8Array(encrypted)
              const combined = new Uint8Array(iv.length + encryptedArray.length)
              combined.set(iv, 0)
              combined.set(encryptedArray, iv.length)
              
              record[field] = `encrypted:${btoa(String.fromCharCode(...combined))}`
            }
          }
        }
      }
    }

    // Add encryption metadata
    encryptedData.metadata.encrypted = true
    encryptedData.metadata.encryption_date = new Date().toISOString()
    encryptedData.metadata.encryption_algorithm = 'AES-256-GCM'

    return encryptedData
  } catch (error) {
    console.error('Error encrypting backup data:', error)
    throw new Error('Failed to encrypt backup data')
  }
}

/**
 * Decrypt backup data
 */
export const decryptBackupData = async (encryptedData, encryptionKey) => {
  try {
    if (!encryptedData.metadata?.encrypted) {
      return encryptedData // Not encrypted, return as-is
    }

    // Import decryption key
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(encryptionKey.padEnd(32, '0').slice(0, 32)),
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    )

    const decryptedData = JSON.parse(JSON.stringify(encryptedData)) // Deep clone

    // Fields that were encrypted
    const sensitiveFields = {
      clients: ['email', 'phone', 'date_of_birth', 'address', 'emergency_contact'],
      medical_histories: ['current_medications', 'allergies', 'chronic_conditions', 'family_history'],
      examinations: ['vital_signs', 'custom_questions'],
      sessions: ['outcome_summary', 'focus_area'],
      progress_notes: ['note', 'rich_text_content', 'plain_text_content'],
    }

    // Decrypt sensitive fields
    for (const [table, fields] of Object.entries(sensitiveFields)) {
      if (decryptedData.data[table]) {
        for (const record of decryptedData.data[table]) {
          for (const field of fields) {
            if (record[field] && typeof record[field] === 'string' && record[field].startsWith('encrypted:')) {
              try {
                const encryptedBase64 = record[field].replace('encrypted:', '')
                const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0))
                
                const iv = combined.slice(0, 12)
                const encrypted = combined.slice(12)
                
                const decrypted = await crypto.subtle.decrypt(
                  { name: 'AES-GCM', iv },
                  key,
                  encrypted
                )

                record[field] = new TextDecoder().decode(decrypted)
              } catch (decryptError) {
                console.error(`Error decrypting field ${field}:`, decryptError)
                record[field] = '[DECRYPTION_ERROR]'
              }
            }
          }
        }
      }
    }

    return decryptedData
  } catch (error) {
    console.error('Error decrypting backup data:', error)
    throw new Error('Failed to decrypt backup data. Please check your encryption key.')
  }
}

/**
 * Generate encryption key from organization ID and user password
 * In production, this should use a proper key derivation function (PBKDF2)
 */
export const generateEncryptionKey = (organizationId, userPassword) => {
  // In production, use PBKDF2 or similar
  // For now, create a deterministic key from org ID + password
  const combined = `${organizationId}-${userPassword}`
  return combined
}

/**
 * Store encryption key securely (in production, use secure key management)
 */
export const storeEncryptionKey = async (organizationId, key) => {
  // In production, use secure key management service
  // For now, store in sessionStorage (temporary)
  sessionStorage.setItem(`backup_key_${organizationId}`, key)
}

/**
 * Retrieve encryption key
 */
export const getEncryptionKey = (organizationId) => {
  // In production, retrieve from secure key management
  return sessionStorage.getItem(`backup_key_${organizationId}`)
}

